import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Truck, Search, X, Check, Star } from "lucide-react";

const FAV_STORAGE_KEY = "podlab:favouriteSuppliers";

/** Read the favourite supplier list from localStorage (best-effort, returns
 *  an empty array on any parse/access failure so the popover still renders). */
const readFavourites = () => {
  try {
    const raw = localStorage.getItem(FAV_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "string") : [];
  } catch {
    return [];
  }
};

/**
 * Supplier filter — presented as a single button that opens a popover with
 * a searchable list of suppliers currently in scope. This keeps the filter
 * row compact even when the supplier catalog is long.
 *
 * Value semantics mirror MaterialChips / CategoryChips:
 *   value === "all"    → no supplier filter
 *   value === "<name>" → only items whose supplier_default matches
 *
 * `relevantNames` narrows the list to suppliers present in the currently
 * scoped item list. When fewer than 2 suppliers are in scope the button
 * disappears entirely (a single-option filter adds no value).
 *
 * Favourites: users can star suppliers to pin them at the top of the list.
 * Persisted in localStorage under a shared key so the whole warehouse gets
 * the same top-3 without needing a backend round-trip.
 */
const SupplierChips = ({
  value,
  onChange,
  testidPrefix = "supplier",
  relevantNames = [],
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [favourites, setFavourites] = useState(() => readFavourites());

  // Sync when localStorage changes from another tab (e.g. staff pinned a
  // supplier on the shared iPad, then Take Stock is opened in another tab).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === FAV_STORAGE_KEY) setFavourites(readFavourites());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggleFavourite = useCallback((name) => {
    setFavourites((prev) => {
      const next = prev.includes(name)
        ? prev.filter((n) => n !== name)
        : [...prev, name];
      try {
        localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* quota / private mode — silently ignore */
      }
      return next;
    });
  }, []);

  const visible = useMemo(
    () =>
      Array.from(new Set(relevantNames.filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [relevantNames],
  );

  // If the active filter drops out of scope, reset to "all" so the user
  // doesn't end up staring at an empty grid.
  useEffect(() => {
    if (value !== "all" && !visible.includes(value)) onChange("all");
  }, [value, visible, onChange]);

  if (visible.length < 2) return null;

  const active = value !== "all";
  const q = query.trim().toLowerCase();
  const searched = q
    ? visible.filter((n) => n.toLowerCase().includes(q))
    : visible;

  // Split favourites to the top (only those actually in scope). Non-favs
  // keep their alphabetical order underneath.
  const favSet = new Set(favourites);
  const favInScope = searched.filter((n) => favSet.has(n));
  const rest = searched.filter((n) => !favSet.has(n));

  const renderRow = (name) => {
    const isActive = value === name;
    const isFav = favSet.has(name);
    return (
      <div
        key={name}
        className={`flex items-center gap-1 rounded-lg transition-colors ${
          isActive ? "bg-[#001740]" : "hover:bg-[#F1E7FA]"
        }`}
      >
        <button
          type="button"
          data-testid={`${testidPrefix}-${name}`}
          onClick={() => {
            onChange(isActive ? "all" : name);
            setOpen(false);
            setQuery("");
          }}
          className={`flex-1 flex items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-left ${
            isActive ? "text-white" : "text-[#001740]"
          }`}
        >
          <span className="truncate">{name}</span>
          {isActive && <Check size={16} strokeWidth={3} />}
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-fav-${name}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleFavourite(name);
          }}
          aria-pressed={isFav}
          aria-label={isFav ? `Unpin ${name}` : `Pin ${name} to top`}
          title={isFav ? "Unpin from top" : "Pin to top"}
          className={`shrink-0 p-1.5 mr-1 rounded-md transition-colors ${
            isActive
              ? "text-white/80 hover:text-white hover:bg-white/10"
              : "text-[#6B6B6B] hover:text-[#FFB300] hover:bg-white"
          }`}
        >
          <Star
            size={16}
            strokeWidth={2.5}
            className={isFav ? "fill-[#FFB300] text-[#FFB300]" : ""}
          />
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testidPrefix}-toggle`}
            aria-pressed={active}
            className={`nb-btn px-3 py-1.5 text-sm transition-all flex items-center gap-1.5 ${
              active ? "nb-btn-dark" : "bg-white"
            }`}
            title={active ? `Filtered by ${value}` : "Filter by supplier"}
          >
            <Truck size={16} strokeWidth={2.5} />
            {active ? value : "Supplier"}
            {active && (
              <span
                role="button"
                tabIndex={0}
                data-testid={`${testidPrefix}-clear`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("all");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange("all");
                  }
                }}
                className="ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 hover:bg-white/20"
                aria-label="Clear supplier filter"
              >
                <X size={14} strokeWidth={3} />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72 p-0 border-2 border-black rounded-xl shadow-[4px_4px_0_#001740] bg-white"
          data-testid={`${testidPrefix}-popover`}
        >
          <div className="p-2 border-b-2 border-black/10">
            <div className="relative">
              <Search
                size={16}
                strokeWidth={2.5}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
              />
              <input
                autoFocus
                data-testid={`${testidPrefix}-search`}
                className="nb-input pl-8 h-9 text-sm w-full"
                placeholder="Find supplier…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
          <div
            className="max-h-64 overflow-y-auto scrollbar-clean p-1.5"
            data-testid={`${testidPrefix}-list`}
          >
            {searched.length === 0 ? (
              <div className="px-3 py-4 text-sm text-[#6B6B6B] text-center">
                No matches
              </div>
            ) : (
              <>
                {favInScope.length > 0 && (
                  <>
                    <div
                      className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider font-bold text-[#6B6B6B] flex items-center gap-1"
                      data-testid={`${testidPrefix}-fav-heading`}
                    >
                      <Star size={11} strokeWidth={3} className="fill-[#FFB300] text-[#FFB300]" />
                      Pinned
                    </div>
                    {favInScope.map(renderRow)}
                    {rest.length > 0 && (
                      <div className="my-1.5 border-t border-black/10" />
                    )}
                  </>
                )}
                {rest.map(renderRow)}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SupplierChips;
